<template>
    <button type="button" class="book-card" :class="{ 'book-card--empty': empty }" :disabled="empty" @click="open">
        <span class="book-icon">
            <v-icon size="20">mdi-calendar-month-outline</v-icon>
        </span>
        <span class="book-text">
            <span class="book-title">{{ $t('business.book_shift_title') }}</span>
            <span class="book-sub">{{ subtitle }}</span>
        </span>
        <v-icon v-if="!empty" size="16" class="book-chevron">mdi-chevron-right</v-icon>
    </button>
</template>

<script>
import { loadSettings, loadShiftBookings, loadShifts, weekStartOf } from "@/utils/Business";

export default {
    name: "BookShiftCard",
    props: {
        companyId: {
            type: String,
            default: null,
        },
        studentId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            shifts: [],
            activities: [],
            bookings: {},
            loaded: false,
        }
    },
    computed: {
        empty(vm = this) {
            return vm.loaded && vm.shifts.length === 0
        },
        booked(vm = this) {
            return vm.shifts.filter(shift => vm.isFixed(shift) || !!vm.bookings[shift.id]?.[vm.studentId])
        },
        next(vm = this) {
            const today = vm.$moment().isoWeekday()
            const now = vm.$moment().format('HH:mm')
            return [...vm.booked]
                .map(shift => {
                    let distance = (shift.day - today + 7) % 7
                    if (distance === 0 && shift.startTime < now) distance = 7
                    return { shift, distance }
                })
                .sort((a, b) => a.distance - b.distance || a.shift.startTime.localeCompare(b.shift.startTime))
                .map(item => item.shift)[0] || null
        },
        subtitle(vm = this) {
            void vm.$i18n.locale
            if (vm.empty) return vm.$t('business.book_shift_none_sub')
            if (!vm.next) return vm.$t('business.book_shift_empty_sub')
            const name = vm.activities.find(item => item.id === vm.next.activityId)?.name
                || vm.$t('business.no_activity')
            const day = vm.$moment().isoWeekday(vm.next.day).format('dddd')
            const fixed = vm.isFixed(vm.next) ? ` · ${vm.$t('business.booking_fixed_short')}` : ''
            const label = `${name} · ${day} ${vm.next.startTime}${fixed}`
            if (vm.booked.length < 2) return label
            return `${label} ${vm.$t('business.book_shift_more', { n: vm.booked.length - 1 })}`
        },
    },
    mounted() {
        this.load()
    },
    methods: {
        isFixed(shift) {
            return !!shift?.members?.[this.studentId]
        },
        async load() {
            const vm = this
            if (!vm.companyId) return
            try {
                const [shifts, settings, bookings] = await Promise.all([
                    loadShifts(vm.companyId),
                    loadSettings(vm.companyId),
                    loadShiftBookings(vm.companyId, weekStartOf()),
                ])
                vm.shifts = shifts
                vm.activities = settings.activities
                vm.bookings = bookings
                vm.loaded = true
            } catch (error) {
                console.error('[business] loadShifts student', error?.code, error?.message)
            }
        },
        open() {
            if (this.empty) return
            this.pushRoute({ name: 'student.shifts' })
        },
    },
}
</script>

<style scoped>
.book-card {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 14px 16px;
    border-radius: 14px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    text-align: left;
    transition: border-color 0.15s;
}

.book-card:hover {
    border-color: var(--v-outline2-base);
}

.book-card--empty {
    cursor: default;
}

.book-card--empty:hover {
    border-color: var(--v-outline-base);
}

.book-card--empty .book-icon {
    background: var(--v-surface2-base);
}

.book-card--empty .book-icon .v-icon,
.book-card--empty .book-title {
    color: var(--v-txtMuted-base);
}

.book-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    border-radius: 10px;
    background: rgba(51, 153, 153, 0.12);
}

.book-icon .v-icon {
    color: var(--v-secondary-base);
}

.book-text {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
}

.book-title {
    font-size: var(--fs-body);
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.book-sub {
    margin-top: 2px;
    font-size: var(--fs-caption);
    color: var(--v-txtMuted-base);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-transform: capitalize;
}

.book-chevron {
    flex-shrink: 0;
    color: var(--v-txtMuted-base) !important;
}
</style>
